import { loadTokens } from "./auth-client";

const BASE =
  import.meta.env.VITE_API_URL ??
  (import.meta.env.DEV ? "http://localhost:8602" : "https://api.pahcertificar.com");

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly code?: string,
    public readonly details?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
};

export async function apiRequest<T = unknown>(
  path: string,
  options: RequestOptions = {}
): Promise<T> {
  const tokens = loadTokens();
  const headers = new Headers(options.headers);
  if (tokens?.access) headers.set("Authorization", `Bearer ${tokens.access}`);
  if (options.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!headers.has("Accept")) headers.set("Accept", "application/json");

  const url = path.startsWith("http") ? path : `${BASE}${path}`;
  const { body: rawBody, ...rest } = options;
  const init: RequestInit = { ...rest, headers };
  if (rawBody !== undefined) {
    init.body = typeof rawBody === "string" ? rawBody : JSON.stringify(rawBody);
  }

  const res = await fetch(url, init);
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const errBody = parsed as { error?: { code?: string; message?: string; details?: unknown } } | null;
    throw new ApiError(
      res.status,
      errBody?.error?.message ?? `HTTP ${res.status}`,
      errBody?.error?.code,
      errBody?.error?.details
    );
  }

  return parsed as T;
}

export async function apiFetchBlob(
  path: string,
  options: Omit<RequestOptions, "body"> = {}
): Promise<{ blob: Blob; fileName: string; contentType: string }> {
  const tokens = loadTokens();
  const headers = new Headers(options.headers);
  if (tokens?.access) headers.set("Authorization", `Bearer ${tokens.access}`);

  const url = path.startsWith("http") ? path : `${BASE}${path}`;
  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = text;
    }
    const errBody = parsed as { error?: { code?: string; message?: string; details?: unknown } } | null;
    throw new ApiError(
      res.status,
      errBody?.error?.message ?? `HTTP ${res.status}`,
      errBody?.error?.code,
      errBody?.error?.details
    );
  }

  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^";]+)"?/.exec(disposition);
  const fileName = match?.[1] ?? "artifact";
  const contentType = res.headers.get("Content-Type") ?? blob.type ?? "application/octet-stream";
  return { blob, fileName, contentType };
}

// ── Identity (onboarding) ──────────────────────────────────────────────────

export type Profile = {
  userId: string;
  phone: string;
  documentType: "NIT" | "CC" | "CE" | "PASAPORTE";
  documentNumber: string;
  fullName: string;
  firstName: string | null;
  secondName: string | null;
  firstSurname: string | null;
  secondSurname: string | null;
  email: string;
  emailVerifiedAt: string;
  createdAt: string;
  updatedAt: string;
};

export type IdentityStartResponse = {
  sessionId: string;
  maskedName: string;
  maskedEmail: string;
  dianName?: string;
  expiresInSeconds: number;
};

export const identityApi = {
  getProfile: () => apiRequest<Profile>("/api/me/profile"),
  start: (input: {
    documentType: Profile["documentType"];
    documentNumber: string;
    /** Sent on retry when DIAN has no record (MANUAL_ENTRY_REQUIRED). */
    fullName?: string;
    firstName?: string;
    secondName?: string;
    firstSurname?: string;
    secondSurname?: string;
    email?: string;
  }) =>
    apiRequest<IdentityStartResponse>("/api/me/identity/start", {
      method: "POST",
      body: input,
    }),
  verify: (input: {
    sessionId: string;
    code: string;
    firstName?: string;
    secondName?: string;
    firstSurname?: string;
    secondSurname?: string;
  }) =>
    apiRequest<Profile>("/api/me/identity/verify", { method: "POST", body: input }),
};

// ── Certificate requests + public artifacts ───────────────────────────────

export type CustomerRequestStatus =
  | "pending"
  | "verified"
  | "issued"
  | "rejected"
  | "revoked"
  | "expired";

export type CustomerArtifact = {
  id: string;
  certificateRequestId: string;
  type: string;
  fileName: string | null;
  encrypted: boolean;
  createdAt: string;
  downloadUrl: string;
};

export type CustomerCertificateRequest = {
  id: string;
  type: string;
  class: string;
  validityYears: number;
  validityMonths: number;
  status: CustomerRequestStatus;
  issuedAt: string | null;
  revokedAt: string | null;
  revocationReason: string | null;
  certificateSerial: string | null;
  validFrom: string | null;
  validUntil: string | null;
  invoice: {
    number: string;
    amountCents: number;
    currency: string;
    issuedAt: string;
  } | null;
  createdAt: string;
  updatedAt: string;
  artifacts: CustomerArtifact[];
};

export type CustomerRequestsResponse = {
  subscriber: {
    id: string;
    name: string;
    documentType: string;
    documentNumber: string;
  } | null;
  requests: CustomerCertificateRequest[];
};

export type CreateCertificateRequestInput = {
  certificateType: CheckoutType;
  /** Validity period being purchased — prices vary by period (PC-35). */
  validityMonths: 3 | 6 | 12 | 24;
  address: string;
  /** DANE code or name — resolved server-side. */
  department: string;
  city: string;
  firstName?: string;
  secondName?: string | null;
  firstSurname?: string;
  secondSurname?: string | null;
};

export type CreateCertificateRequestResponse = {
  subscriber: {
    id: string;
    name: string;
    documentType: string;
    documentNumber: string;
  };
  request: CustomerCertificateRequest;
};

export type CustomerDocType = "cedula" | "rut" | "camara_comercio";

export type DocValidationStatus = "pass" | "warn" | "fail";

export type DocValidationCheck = {
  id: string;
  label: string;
  status: DocValidationStatus;
  detail?: string;
};

/** AI document-validation verdict (PC-29). Advisory — never blocks the wizard. */
export type DocValidation = {
  detectedType: string;
  legible: boolean;
  overall: DocValidationStatus;
  checks: DocValidationCheck[];
  extracted: {
    name?: string | null;
    documentNumber?: string | null;
    nit?: string | null;
    issueDate?: string | null;
  };
};

export type UploadedCustomerDoc = {
  id: string;
  type: string;
  fileName: string | null;
  visibility: string;
  createdAt: string;
  downloadUrl: string;
  /** null when AI validation was skipped (e.g. AI outage — fail open). */
  validation?: DocValidation | null;
};

export const certificateRequestsApi = {
  list: () => apiRequest<CustomerRequestsResponse>("/api/me/certificate-requests"),
  /** Persist the request server-side (creates the subscriber + a pending request). */
  create: (input: CreateCertificateRequestInput) =>
    apiRequest<CreateCertificateRequestResponse>("/api/me/certificate-requests", {
      method: "POST",
      body: input,
    }),
  /**
   * Upload a supporting document to R2 (same pipeline the admin uses), linked to
   * the request and stored visibility=public so the customer can see it back.
   */
  uploadDocument: async (
    certificateRequestId: string,
    file: File,
    type: CustomerDocType,
    fileName?: string
  ): Promise<UploadedCustomerDoc> => {
    const tokens = loadTokens();
    const form = new FormData();
    form.append("file", file);
    form.append("type", type);
    form.append("certificateRequestId", certificateRequestId);
    if (fileName) form.append("fileName", fileName);
    // No explicit Content-Type — the browser sets the multipart boundary.
    const headers = new Headers({ Accept: "application/json" });
    if (tokens?.access) headers.set("Authorization", `Bearer ${tokens.access}`);
    const res = await fetch(`${BASE}/api/me/artifacts/upload`, { method: "POST", headers, body: form });
    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = text;
    }
    if (!res.ok) {
      const errBody = parsed as { error?: { code?: string; message?: string } } | null;
      throw new ApiError(res.status, errBody?.error?.message ?? `HTTP ${res.status}`, errBody?.error?.code);
    }
    return parsed as UploadedCustomerDoc;
  },
  downloadArtifact: (artifactId: string) =>
    apiFetchBlob(`/api/me/artifacts/${encodeURIComponent(artifactId)}/download`),
};

// ── Server-side wizard draft (resumable request) ────────────────────────────

export type RequestDraftPayload = {
  step?: number;
  /** May hold a retired catalog type on drafts saved before PC-35. */
  type?: string | null;
  name?: string;
  firstName?: string;
  secondName?: string;
  firstSurname?: string;
  secondSurname?: string;
  docType?: "CC" | "CE" | "PA" | "NIT";
  docNumber?: string;
  email?: string;
  phone?: string;
  city?: string;
  department?: string;
  address?: string;
  /** Legacy pre-PC-35 drafts stored years here; new drafts use validityMonths. */
  validity?: 1 | 2 | 3;
  validityMonths?: 3 | 6 | 12 | 24;
  /** Lazily-created pending request the wizard's docs upload against. */
  requestId?: string | null;
  /** Supporting documents already uploaded to R2 (resumable across devices). */
  uploaded?: { type: CustomerDocType; name: string }[];
};

export const requestDraftApi = {
  get: () =>
    apiRequest<{ draft: RequestDraftPayload | null; updatedAt: string | null }>(
      "/api/me/request-draft"
    ),
  save: (draft: RequestDraftPayload) =>
    apiRequest<{ ok: true; updatedAt: string }>("/api/me/request-draft", {
      method: "PUT",
      body: { draft },
    }),
  clear: () => apiRequest<{ ok: true }>("/api/me/request-draft", { method: "DELETE" }),
};

// ── Payments ───────────────────────────────────────────────────────────────

// Only types on the PC-35 price list are purchasable.
export type CheckoutType =
  | "firma_natural"
  | "firma_representante"
  | "firma_juridica"
  | "facturacion";

export type CheckoutResponse = {
  paymentLinkId: string;
  token: string;
  /** null for push providers (Nequi); the customer stays in-app and polls. */
  checkoutUrl: string | null;
  amountCop: number;
  currency: "COP";
  flow: "push" | "redirect";
};

export type PaymentStatusResponse = {
  linkStatus: "active" | "started" | "consumed" | "expired" | "cancelled";
  intentStatus: "created" | "pending" | "authorized" | "succeeded" | "failed" | "cancelled" | null;
  paid: boolean;
  checkoutUrl: string | null;
  /** Charged amount in COP — null when the payment service predates PC-35. */
  amountCop: number | null;
};

export type CreateCheckoutInput = {
  certificateType: CheckoutType;
  /** Validity period being purchased — prices vary by period (PC-35). */
  validityMonths: 3 | 6 | 12 | 24;
  /** Override the Nequi-bound phone — defaults to the WhatsApp phone server-side. */
  nequiPhone?: string;
  /** Ties the checkout to a server-side request so it's flagged paid (PC-24). */
  certificateRequestId?: string;
};

export const paymentsApi = {
  createCheckout: (input: CreateCheckoutInput) =>
    apiRequest<CheckoutResponse>("/api/me/payments/checkout", {
      method: "POST",
      body: input,
    }),
  status: (token: string) =>
    apiRequest<PaymentStatusResponse>(
      `/api/me/payments/${encodeURIComponent(token)}`
    ),
};
